import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { API_URL } from "../api";
import HeroWorld from "./HeroWorld";
import "./public.css";
const Editor = lazy(() => import("../App"));
type User = {
  id: string;
  email: string;
  name: string;
  role: "member" | "superadmin";
  status: "pending" | "approved" | "suspended" | "rejected";
  verified: boolean;
  createdAt: number;
};
type Session = { mode: "local" | "production"; user: User | null };
type Screen =
  | "home"
  | "login"
  | "register"
  | "forgot"
  | "verify"
  | "reset"
  | "account"
  | "users"
  | "studio";
const link = new URLSearchParams(location.hash.slice(1));
const token = link.get("reset") || link.get("verify") || "";
const tokenScreen: Screen | null = link.has("reset")
  ? "reset"
  : link.has("verify")
    ? "verify"
    : null;
if (tokenScreen) history.replaceState(null, "", location.pathname);
async function api<T>(
  path: string,
  body?: unknown,
  method = "POST",
): Promise<T> {
  const response = await fetch(`${API_URL}/auth${path}`, {
    credentials: "same-origin",
    cache: "no-store",
    ...(body === undefined
      ? {}
      : {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
  });
  const data = await response.json();
  if (!response.ok)
    throw new Error(data.error?.message || "Richiesta non riuscita.");
  return data as T;
}
function clearPrivateCache() {
  for (const key of Object.keys(localStorage))
    if (key.startsWith("quay.")) localStorage.removeItem(key);
}
export default function Access() {
  const identity = useRef<string | null>(
    localStorage.getItem("quay.account.owner"),
  );
  const [session, setSession] = useState<Session | null>(null),
    [error, setError] = useState("");
  const [screen, setScreen] = useState<Screen>(
    tokenScreen ?? (location.pathname === "/studio" ? "studio" : "home"),
  );
  const refresh = async () => {
    try {
      const next = await api<Session>("/me");
      const id = next.user?.id ?? null;
      if (next.mode === "production" && identity.current !== id)
        clearPrivateCache();
      if (next.mode === "production" && id)
        localStorage.setItem("quay.account.owner", id);
      identity.current = id;
      setSession(next);
      setError("");
    } catch {
      setSession(null);
      setError("Il servizio non è raggiungibile. Riprova tra poco.");
    }
  };
  useEffect(() => {
    void refresh();
    const focus = () => void refresh();
    window.addEventListener("focus", focus);
    const timer = setInterval(focus, 60000);
    return () => {
      window.removeEventListener("focus", focus);
      clearInterval(timer);
    };
  }, []);
  const go = (next: Screen) => {
    setScreen(next);
    history.replaceState(null, "", next === "studio" ? "/studio" : "/");
  };
  const logout = async () => {
    await api("/logout", {});
    clearPrivateCache();
    await refresh();
    go("home");
  };
  const approved =
    session?.user?.verified && session.user.status === "approved";
  if (!session)
    return (
      <div className="access-loading" role="status">
        {error || "Connessione a QUAY…"}
        {error && <button onClick={() => void refresh()}>Riprova</button>}
      </div>
    );
  if (session.mode === "local")
    return (
      <Suspense
        fallback={<div className="access-loading">Caricamento editor…</div>}
      >
        <Editor />
      </Suspense>
    );
  if (screen === "studio" && approved)
    return (
      <div className="production-studio">
        <header className="account-bar">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              go("home");
            }}
          >
            QUAY / Home
          </a>
          <span>{session.user!.name} · spazio privato</span>
          <button onClick={() => go("account")}>Account</button>
          {session.user!.role === "superadmin" && (
            <button onClick={() => go("users")}>Utenti</button>
          )}
          <button
            onClick={() => void logout().catch((e) => setError(String(e)))}
          >
            Esci
          </button>
          {error && <span role="alert">{error}</span>}
        </header>
        <Suspense
          fallback={<div className="access-loading">Caricamento editor…</div>}
        >
          <Editor
            key={session.user!.id}
            canDeploy={session.user!.role === "superadmin"}
          />
        </Suspense>
      </div>
    );
  return (
    <div className="public-site">
      <header className="public-header">
        <a
          className="public-logo"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            go("home");
          }}
        >
          Q<span>UAY</span>
          <i>WORLD STUDIO</i>
        </a>
        <nav aria-label="Navigazione principale">
          <a href="#environments" onClick={() => go("home")}>
            Gli ambienti
          </a>
          {session.user ? (
            <button onClick={() => go("account")}>Il mio account</button>
          ) : (
            <button onClick={() => go("login")}>Accedi</button>
          )}
          <button
            className="public-primary small"
            onClick={() => go(approved ? "studio" : "register")}
          >
            {approved ? "Apri studio" : "Richiedi accesso"} <span>↗</span>
          </button>
        </nav>
      </header>
      {screen === "home" ? (
        <>
          <section className="public-hero">
            <HeroWorld />
            <div className="hero-copy">
              <p className="eyebrow">
                <span /> WORDS BECOME WORLDS
              </p>
              <h1>
                Non immaginare
                <br />
                soltanto.
                <br />
                <em>Entra.</em>
              </h1>
              <p className="hero-intro">
                Dalle parole allo spazio. Dallo spazio a una storia.
                <br />
                Il tuo universo 3D, pronto per il web.
              </p>
              <div className="hero-actions">
                <button
                  className="public-primary"
                  onClick={() => go(approved ? "studio" : "register")}
                >
                  Inizia a costruire <span>↗</span>
                </button>
                <a href="#environments">Esplora gli ambienti ↓</a>
              </div>
            </div>
            <div className="world-caption">
              <span>LIVE / UN MONDO DI POSSIBILITÀ</span>
              <span>01 — POSSIBILITÀ INFINITE</span>
            </div>
            <div className="hero-coordinates">
              X 12.00 / Y 9.00 / Z 14.00
              <br />
              IMMAGINA · ESPLORA
            </div>
          </section>
          <section id="environments" className="public-environments">
            <div className="section-intro">
              <p className="eyebrow">UN SOLO FLUSSO CREATIVO</p>
              <h2>
                Quattro ambienti.
                <br />
                <span>Nessun limite all’immaginazione.</span>
              </h2>
              <p>
                Componi con i tuoi modelli, costruisci gli spazi, dirigine
                l’esperienza e dai al mondo una casa sul web.
              </p>
            </div>
            <div className="environment-grid">
              {[
                [
                  "01",
                  "QWAY",
                  "Componi la materia",
                  "Le tue parole e i tuoi asset GLB diventano scene concrete.",
                  "◈",
                ],
                [
                  "02",
                  "WRIGHT",
                  "Disegna lo spazio",
                  "Stanze, passaggi e architetture prendono forma attorno alle tue idee.",
                  "▧",
                ],
                [
                  "03",
                  "RIDLEY",
                  "Dirigi la storia",
                  "Personaggi, inquadrature e interazioni danno vita al mondo.",
                  "◎",
                ],
                [
                  "04",
                  "RAMS",
                  "Apri le porte",
                  "Un sito completo, dal display desktop allo smartphone.",
                  "↗",
                ],
              ].map(([n, title, subtitle, text, icon]) => (
                <article key={title}>
                  <div className="environment-number">
                    {n}
                    <span>{icon}</span>
                  </div>
                  <h3>{title}</h3>
                  <h4>{subtitle}</h4>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </section>
          <section className="public-invite">
            <p className="eyebrow">IL PROSSIMO MONDO È IL TUO</p>
            <h2>
              C’è un luogo che
              <br />
              ancora non esiste.
            </h2>
            <button
              className="public-primary"
              onClick={() => go(approved ? "studio" : "register")}
            >
              Costruiamolo <span>↗</span>
            </button>
            <p>Accesso su approvazione. Uno spazio privato per ogni autore.</p>
          </section>
        </>
      ) : screen === "users" && session.user?.role === "superadmin" ? (
        <Users />
      ) : (
        <Account
          key={screen}
          screen={
            screen === "studio"
              ? session.user
                ? "account"
                : "login"
              : screen === "account" && !session.user
                ? "login"
                : screen
          }
          user={session.user}
          go={go}
          refresh={refresh}
          logout={logout}
        />
      )}
      <footer className="public-footer">
        <strong>QUAY</strong>
        <span>Immagina. Componi. Abita.</span>
        <span>QWAY / WRIGHT / RIDLEY / RAMS</span>
      </footer>
    </div>
  );
}
function Account({
  screen,
  user,
  go,
  refresh,
  logout,
}: {
  screen: Screen;
  user: User | null;
  go: (s: Screen) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [error, setError] = useState("");
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    const values = Object.fromEntries(new FormData(event.currentTarget));
    try {
      if (screen === "login") {
        await api("/login", values);
        clearPrivateCache();
        await refresh();
        go("studio");
      } else {
        const path = screen === "account" ? "password" : screen;
        const result = await api<{ message: string }>(
          `/${path}`,
          screen === "reset"
            ? { token, password: values.password }
            : screen === "verify"
              ? { token }
              : values,
        );
        setMessage(result.message);
        if (screen === "account") {
          clearPrivateCache();
          await refresh();
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const resend = async () => {
    setBusy(true);
    try {
      const result = await api<{ message: string }>("/resend", {
        email: user!.email,
      });
      setMessage(result.message);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };
  const titles: Record<string, string> = {
    login: "Bentornato.",
    register: "Il tuo prossimo mondo.",
    forgot: "Ritrova l’accesso.",
    reset: "Una nuova password.",
    verify: "Conferma la tua email.",
    account: "Il tuo spazio.",
  };
  return (
    <section className="account-page">
      <div>
        <p className="eyebrow">QUAY / ACCESSO ALLO STUDIO</p>
        <h1>{titles[screen] || "Il tuo account."}</h1>
        <p>
          {screen === "register"
            ? "Verifica la tua email. Dopo l’approvazione, il tuo studio privato sarà pronto."
            : "Le tue idee meritano uno spazio tutto loro."}
        </p>
        <div className="account-orbit" aria-hidden="true">
          Q
        </div>
      </div>
      <div className="account-card">
        {screen === "account" && user && (
          <>
            <h2>{user.name}</h2>
            <p>{user.email}</p>
            <p className="account-state">
              {!user.verified
                ? "Verifica la tua email per continuare."
                : user.status === "approved"
                  ? "Accesso approvato · spazio privato"
                  : user.status === "pending"
                    ? "La tua richiesta è in attesa di approvazione."
                    : "Accesso non attivo. Contatta l’amministratore."}
            </p>
            {!user.verified && (
              <button disabled={busy} onClick={() => void resend()}>
                Invia nuovamente la verifica
              </button>
            )}
            {user.verified && user.status === "approved" && (
              <button className="public-primary" onClick={() => go("studio")}>
                Apri studio ↗
              </button>
            )}
            {user.role === "superadmin" && (
              <button onClick={() => go("users")}>Gestione utenti</button>
            )}
            <button onClick={() => void refresh()}>Aggiorna stato</button>
            <button
              onClick={() => void logout().catch((e) => setError(String(e)))}
            >
              Esci
            </button>
            <h3>Cambia password</h3>
          </>
        )}
        <form
          onSubmit={(event) => void submit(event)}
          aria-label="Account form"
        >
          {screen === "register" && (
            <label>
              Nome
              <input name="name" autoComplete="name" required maxLength={100} />
            </label>
          )}
          {["login", "register", "forgot"].includes(screen) && (
            <label>
              Email
              <input
                name="email"
                type="email"
                autoComplete="email"
                required
                maxLength={254}
              />
            </label>
          )}
          {screen === "account" && (
            <label>
              Password attuale
              <input
                name="current"
                type="password"
                autoComplete="current-password"
                required
                maxLength={128}
              />
            </label>
          )}
          {["login", "register", "reset", "account"].includes(screen) && (
            <label>
              {screen === "login" ? "Password" : "Nuova password"}
              <input
                name="password"
                type="password"
                autoComplete={
                  screen === "login" ? "current-password" : "new-password"
                }
                required
                minLength={screen === "login" ? 1 : 15}
                maxLength={128}
              />
              {screen !== "login" && (
                <small>Almeno 15 caratteri. Puoi usare una frase.</small>
              )}
            </label>
          )}
          {screen === "verify" && (
            <p>
              Conferma per verificare questo indirizzo email. Il link è monouso.
            </p>
          )}
          <button disabled={busy} className="public-primary" type="submit">
            {busy
              ? "Attendi…"
              : {
                  login: "Accedi",
                  register: "Richiedi accesso",
                  forgot: "Invia il link",
                  reset: "Imposta password",
                  verify: "Verifica email",
                  account: "Cambia password",
                  home: "Continua",
                  studio: "Continua",
                  users: "Continua",
                }[screen] || "Continua"}{" "}
            <span>↗</span>
          </button>
        </form>
        {message && (
          <p role="status" className="account-message">
            {message}
          </p>
        )}
        {error && (
          <p role="alert" className="account-error">
            {error}
          </p>
        )}
        <div className="account-links">
          {screen === "login" ? (
            <>
              <button onClick={() => go("forgot")}>
                Password dimenticata?
              </button>
              <button onClick={() => go("register")}>
                Non hai un account?
              </button>
            </>
          ) : (
            <button onClick={() => go("login")}>Torna all’accesso</button>
          )}
        </div>
      </div>
    </section>
  );
}
function Users() {
  const [users, setUsers] = useState<User[]>([]),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [query, setQuery] = useState("");
  const load = async () => {
    try {
      setUsers(await api<User[]>("/users"));
    } catch (e) {
      setError(String(e));
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const update = async (user: User, status: string) => {
    setBusy(true);
    setError("");
    try {
      await api(`/users/${user.id}`, { status }, "PATCH");
      await load();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="user-management">
      <p className="eyebrow">AMMINISTRAZIONE</p>
      <h1>Persone, prima dei mondi.</h1>
      <p>
        Approva gli indirizzi verificati. Sospensione e rifiuto revocano tutte
        le sessioni; i progetti privati vengono conservati.
      </p>
      <label>
        Cerca utenti
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Nome o email"
        />
      </label>
      {error && <p role="alert">{error}</p>}
      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Utente</th>
              <th>Email</th>
              <th>Stato</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {users
              .filter((u) =>
                (u.name + u.email).toLowerCase().includes(query.toLowerCase()),
              )
              .map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.name}
                    <small>{u.role}</small>
                  </td>
                  <td>
                    {u.email}
                    <small>
                      {u.verified ? "Email verificata" : "Email da verificare"}
                    </small>
                  </td>
                  <td>{u.status}</td>
                  <td>
                    {u.role !== "superadmin" && (
                      <>
                        <button
                          disabled={
                            busy || !u.verified || u.status === "approved"
                          }
                          onClick={() => void update(u, "approved")}
                        >
                          Approva
                        </button>
                        <button
                          disabled={busy || u.status === "suspended"}
                          onClick={() => void update(u, "suspended")}
                        >
                          Sospendi
                        </button>
                        <button
                          disabled={busy || u.status === "rejected"}
                          onClick={() => void update(u, "rejected")}
                        >
                          Rifiuta
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
